import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { imbsAPI, customerAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  RefreshCw, CheckCircle, XCircle, Package, Users,
  Download, Search, Link2, Unlink, X, Activity, Wifi, Settings,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const IMPORT_FIELDS = [
  { key: 'mobile',      label: 'Mobile Number',    note: 'mobile_no → Customer.mobile' },
  { key: 'email',       label: 'Email',            note: 'email → Customer.email' },
  { key: 'username',    label: 'PPPoE Username',   note: 'username → broadband.username' },
  { key: 'address',     label: 'Address',          note: 'address → address.line1' },
  { key: 'city',        label: 'City / Area',      note: 'city → address.city + area' },
  { key: 'group',       label: 'User Group (Area)',    note: 'group → address.area' },
  { key: 'pincode',     label: 'Pincode',          note: 'zip → address.pincode' },
  { key: 'coordinates', label: 'GPS Coordinates',  note: 'gps_lat/gps_long → address.coordinates' },
  { key: 'expiryDate',  label: 'Expiry Date',      note: 'expiry_date → broadband.expiryDate' },
  { key: 'status',      label: 'Active Status',    note: 'enabled/status → Customer.status' },
  { key: 'plan',        label: 'Package / Plan',   note: 'package.name → match to local BroadbandPlan' },
];

const card = {
  background: 'var(--cb-ffffff)',
  border: '1px solid var(--cb-e5e7eb)',
  borderRadius: 8,
  padding: 24,
  marginBottom: 16,
};
const sectionTitle = {
  fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.06em', color: 'var(--c-6b7280)',
};
const badge = (ok) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '4px 12px', borderRadius: 4, fontSize: '.78rem', fontWeight: 600,
  background: ok ? 'var(--cb-dcfce7)' : 'var(--cb-fee2e2)', color: ok ? 'var(--c-16a34a)' : 'var(--c-dc2626)',
});

// ─── Link Modal ───────────────────────────────────────────────────────────────
function LinkModal({ customer, onClose, onLinked }) {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking]     = useState(null);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const params = /^\d+$/.test(q.trim()) ? { mobile_no: q.trim() } : { username: q.trim() };
      const { data } = await imbsAPI.getSubscribers(params);
      const list = data.data?.data?.data || data.data?.data || data.data || [];
      setResults(Array.isArray(list) ? list : []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  const handleLink = async (sub) => {
    const subId = String(sub.id || sub.subscriber_id || '');
    if (!subId) return;
    setLinking(subId);
    try {
      await imbsAPI.linkCustomer(customer._id, subId);
      toast.success(`${customer.name} linked to Netzur #${subId}`);
      onLinked(customer._id, subId);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Link failed');
    } finally { setLinking(null); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1rem' }}>
            Link <strong>{customer.name}</strong> to Netzur Subscriber
          </h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-9ca3af)', pointerEvents: 'none' }} />
            <input
              className="form-control"
              style={{ paddingLeft: 30 }}
              placeholder="Search Netzur by username or mobile…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>

          {searching && (
            <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--c-9ca3af)', fontSize: '.83rem' }}>
              <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} />Searching Netzur…
            </div>
          )}
          {!searching && results.length === 0 && query.length >= 2 && (
            <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--c-9ca3af)', fontSize: '.83rem' }}>No subscribers found</div>
          )}

          {results.length > 0 && (
            <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, overflow: 'hidden' }}>
              {results.map((sub, i) => {
                const subId = String(sub.id || sub.subscriber_id || '');
                return (
                  <div key={subId || i} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px',
                    borderBottom: i < results.length - 1 ? '1px solid var(--cb-f3f4f6)' : 'none',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '.88rem' }}>{sub.name || sub.fullname || sub.full_name || '—'}</div>
                      <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginTop: 2 }}>
                        #{subId} · {sub.username || '—'} · {sub.mobile_no || sub.mobile || '—'}
                        {sub.package?.name && <span style={{ marginLeft: 8 }}>· {sub.package.name}</span>}
                      </div>
                    </div>
                    <span style={{ ...badge(sub.status === 'active'), padding: '2px 8px', fontSize: '.72rem' }}>{sub.status || '—'}</span>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ padding: '4px 12px', fontSize: '.78rem' }}
                      disabled={linking === subId}
                      onClick={() => handleLink(sub)}
                    >
                      {linking === subId ? 'Linking…' : 'Link'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {!query && (
            <p style={{ fontSize: '.8rem', color: 'var(--c-9ca3af)', margin: 0 }}>
              Type a username or mobile number to search Netzur subscribers.
            </p>
          )}
        </div>
        <div style={{ padding: '0 24px 20px', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function IMBSPage() {
  useDarkReady();
  const navigate = useNavigate();

  // Connection
  const [status, setStatus]               = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // Packages
  const [packages, setPackages]           = useState([]);
  const [pkgLoading, setPkgLoading]       = useState(false);
  const [importing, setImporting]         = useState(false);

  // Subscriber lookup
  const [subscribers, setSubscribers]     = useState([]);
  const [subLoading, setSubLoading]       = useState(false);
  const [subSearch, setSubSearch]         = useState('');

  // ── Local Customers ──
  const [customers, setCustomers]         = useState([]);
  const [custLoading, setCustLoading]     = useState(false);
  const [custLoaded, setCustLoaded]       = useState(false);
  const [custFilter, setCustFilter]       = useState('all'); // all | linked | unlinked
  const [custSearch, setCustSearch]       = useState('');
  const [linkTarget, setLinkTarget]       = useState(null);
  const [unlinking, setUnlinking]         = useState(null);
  const [pullingAddress, setPullingAddress] = useState(false);
  const [pullAddressResult, setPullAddressResult] = useState(null);
  const [syncingStatus, setSyncingStatus] = useState(false);
  const [syncStatusResult, setSyncStatusResult] = useState(null);

  // ── Netzur Subscribers (bulk) ──
  const [imbsCusts, setImbsCusts]               = useState([]);
  const [imbsCustLoading, setImbsCustLoading]   = useState(false);
  const [imbsCustLoaded, setImbsCustLoaded]     = useState(false);
  const [imbsCustSearch, setImbsCustSearch]     = useState('');
  const [imbsImporting, setImbsImporting]       = useState(false);

  // ── Field selection ──
  const [importFields, setImportFields]         = useState(() => new Set(IMPORT_FIELDS.map(f => f.key)));
  const [showFieldPicker, setShowFieldPicker]   = useState(false);

  // Tab: 'local' | 'imbs'
  const [custTab, setCustTab]             = useState('local');

  const testConnection = useCallback(async () => {
    setStatusLoading(true);
    setStatus(null);
    try {
      const { data } = await imbsAPI.getStatus();
      setStatus({ ok: true, data: data.data });
      toast.success('Netzur connection successful');
    } catch (err) {
      setStatus({ ok: false, message: err.response?.data?.message || err.message });
      toast.error('Netzur connection failed');
    } finally { setStatusLoading(false); }
  }, []);

  const loadPackages = async () => {
    setPkgLoading(true);
    try {
      const { data } = await imbsAPI.getPackages();
      const list = data.data?.data || data.data || (Array.isArray(data.data) ? data.data : []);
      setPackages(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load packages');
    } finally { setPkgLoading(false); }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await imbsAPI.importPackages();
      toast.success(data.message || `Imported ${data.created} plans`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally { setImporting(false); }
  };

  const searchSubscribers = async () => {
    if (!subSearch.trim()) return;
    setSubLoading(true);
    try {
      const params = /^\d+$/.test(subSearch.trim()) ? { mobile_no: subSearch.trim() } : { username: subSearch.trim() };
      const { data } = await imbsAPI.getSubscribers(params);
      const list = data.data?.data?.data || data.data?.data || data.data || [];
      setSubscribers(Array.isArray(list) ? list : []);
      if (!list.length) toast('No subscribers found', { icon: '🔍' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Search failed');
    } finally { setSubLoading(false); }
  };

  // ── Load local customers ─────────────────────────────────────────────────────
  const loadCustomers = async () => {
    setCustLoading(true);
    try {
      const { data } = await customerAPI.getAll({ limit: 500, sort: 'name' });
      const list = data?.data?.docs || data?.data || [];
      const bb = list.filter(c => c.customerType === 'broadband' || c.customerType === 'both');
      setCustomers(bb);
      setCustLoaded(true);
    } catch { toast.error('Failed to load customers'); }
    finally { setCustLoading(false); }
  };

  const handleUnlink = async (customer) => {
    if (!window.confirm(`Unlink "${customer.name}" from Netzur?`)) return;
    setUnlinking(customer._id);
    try {
      await imbsAPI.unlinkCustomer(customer._id);
      toast.success(`${customer.name} unlinked from Netzur`);
      setCustomers(cs => cs.map(c => c._id === customer._id ? { ...c, imbs: undefined } : c));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unlink failed');
    } finally { setUnlinking(null); }
  };

  const handleLinked = (customerId, subscriberId) => {
    setCustomers(cs => cs.map(c =>
      c._id === customerId ? { ...c, imbs: { subscriberId, lastSynced: new Date().toISOString() } } : c
    ));
  };

  // Pulls Netzur's on-file address and REPLACES Customer.billingAddress with it outright
  // (not a merge) for every customer already linked to Netzur (imbs.subscriberId set) —
  // bulk version of the per-customer "Pull Address" action, server-side, independent of
  // whatever's currently loaded in this page.
  const handlePullAddressBulk = async () => {
    if (!window.confirm('This replaces the Billing Address on every Netzur-linked customer with whatever Netzur has on file, overwriting any existing billing address. Continue?')) return;
    setPullingAddress(true);
    setPullAddressResult(null);
    try {
      const { data } = await imbsAPI.pullAddressBulk();
      setPullAddressResult(data.data);
      const { pulled, total } = data.data;
      if (pulled > 0) toast.success(`Replaced billing address for ${pulled} of ${total} Netzur-linked customer${total === 1 ? '' : 's'}`);
      else toast('No addresses replaced — see details below', { icon: 'ℹ️' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto-pull & replace failed');
    } finally {
      setPullingAddress(false);
    }
  };

  // Bulk version of the per-customer "Sync Status" button (CustomerDetailPage.js) — pulls
  // expiry_date/package/isActive from Netzur into broadband.expiryDate + imbs.* for every
  // Netzur-linked customer. This is what keeps expiry-driven stats (Dashboard renewal
  // counts, Renewals page) matching Netzur's real billing dates instead of drifting stale.
  const handleSyncStatusBulk = async () => {
    setSyncingStatus(true);
    setSyncStatusResult(null);
    try {
      const { data } = await imbsAPI.syncStatusBulk();
      setSyncStatusResult(data.data);
      const { synced, total } = data.data;
      if (synced > 0) toast.success(`Synced status & expiry for ${synced} of ${total} Netzur-linked customer${total === 1 ? '' : 's'}`);
      else toast('Nothing synced — see details below', { icon: 'ℹ️' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed');
    } finally {
      setSyncingStatus(false);
    }
  };

  const linkedCount   = customers.filter(c => c.imbs?.subscriberId).length;
  const unlinkedCount = customers.filter(c => !c.imbs?.subscriberId).length;

  const filteredCustomers = customers.filter(c => {
    const matchFilter =
      custFilter === 'all' ||
      (custFilter === 'linked' && c.imbs?.subscriberId) ||
      (custFilter === 'unlinked' && !c.imbs?.subscriberId);
    const q = custSearch.toLowerCase();
    const matchSearch = !q ||
      (c.name || '').toLowerCase().includes(q) ||
      (c.mobile || '').includes(q) ||
      (c.customerId || '').toLowerCase().includes(q) ||
      (c.broadband?.pppoeUsername || '').toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  // ── Load Netzur subscribers (bulk) ─────────────────────────────────────────────
  const loadImbsCustomers = async () => {
    setImbsCustLoading(true);
    try {
      const { data } = await imbsAPI.getSubscribers({ per_page: 500 });
      const list = data.data?.data?.data || data.data?.data || data.data || [];
      setImbsCusts(Array.isArray(list) ? list : []);
      setImbsCustLoaded(true);
      if (!list.length) toast('No subscribers returned from Netzur', { icon: '🔍' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Netzur subscribers');
    } finally { setImbsCustLoading(false); }
  };

  const handleImportSubscribers = async () => {
    setImbsImporting(true);
    try {
      const { data } = await imbsAPI.importSubscribers([...importFields]);
      toast.success(data.message || `Imported ${data.created} customers`);
      if (custLoaded) loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally { setImbsImporting(false); }
  };

  const toggleField = (key) => {
    setImportFields(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Map: imbsSubscriberId → local customer (for "Linked To" column)
  const imbsLinkMap = {};
  customers.forEach(c => {
    if (c.imbs?.subscriberId) imbsLinkMap[String(c.imbs.subscriberId)] = c;
  });

  const filteredImbsCusts = imbsCusts.filter(s => {
    const q = imbsCustSearch.toLowerCase();
    if (!q) return true;
    const subId = String(s.id || s.subscriber_id || '');
    return (
      (s.name || s.fullname || s.full_name || '').toLowerCase().includes(q) ||
      (s.username || '').toLowerCase().includes(q) ||
      (s.mobile_no || s.mobile || '').includes(q) ||
      subId.includes(q)
    );
  });

  const imbsLinkedCount   = imbsCusts.filter(s => imbsLinkMap[String(s.id || s.subscriber_id || '')]).length;
  const imbsUnlinkedCount = imbsCusts.length - imbsLinkedCount;

  useEffect(() => { testConnection(); }, [testConnection]);

  const tabBtn = (active) => ({
    padding: '5px 16px', borderRadius: 6, border: '1px solid',
    cursor: 'pointer', fontSize: '.82rem', fontWeight: active ? 700 : 400,
    background: active ? 'var(--primary)' : 'var(--cb-f9fafb)',
    color: active ? 'var(--c-ffffff)' : 'var(--c-374151)',
    borderColor: active ? 'var(--primary)' : 'var(--cb-d1d5db)',
    transition: 'all .12s',
  });

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>Netzur Integration</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--c-6b7280)', fontSize: '.88rem' }}>
          Netzur ISP Billing System — subscriber sync, packages, sessions
        </p>
      </div>

      {/* ── Connection Status ── */}
      <div style={card}>
        <div style={{ ...sectionTitle, display: 'block', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid var(--cb-e5e7eb)' }}>Connection Status</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {status === null && !statusLoading && (
            <span style={{ color: 'var(--c-9ca3af)', fontSize: '.88rem' }}>Not checked yet</span>
          )}
          {statusLoading && (
            <span style={{ color: 'var(--c-6b7280)', fontSize: '.88rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Testing…
            </span>
          )}
          {status && !statusLoading && (
            <span style={badge(status.ok)}>
              {status.ok ? <CheckCircle size={13} /> : <XCircle size={13} />}
              {status.ok ? 'Connected' : 'Failed'}
            </span>
          )}
          {status?.ok && status.data && (
            <span style={{ fontSize: '.82rem', color: 'var(--c-6b7280)' }}>
              Site: <strong>{status.data?.site?.name || status.data?.name || JSON.stringify(status.data).slice(0, 60)}</strong>
            </span>
          )}
          {status?.message && (
            <span style={{ fontSize: '.82rem', color: 'var(--c-dc2626)' }}>{status.message}</span>
          )}
          <button className="btn btn-secondary btn-sm" onClick={testConnection} disabled={statusLoading}>
            <RefreshCw size={13} /> Test Connection
          </button>
        </div>
        {!status?.ok && (
          <p style={{ marginTop: 12, marginBottom: 0, fontSize: '.82rem', color: 'var(--c-6b7280)', background: 'var(--cb-f9fafb)', padding: '10px 14px', borderRadius: 4 }}>
            Add <code>IMBS_TOKEN</code> and <code>IMBS_SITE_ID</code> to your <code>.env</code> file on the server, then restart the API.
          </p>
        )}
      </div>

      {/* ── Customers Sync ── */}
      <div style={card}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={sectionTitle}>Customers</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={tabBtn(custTab === 'local')} onClick={() => setCustTab('local')}>
                <Users size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Local {custLoaded ? `(${customers.length})` : ''}
              </button>
              <button style={tabBtn(custTab === 'imbs')} onClick={() => setCustTab('imbs')}>
                <Wifi size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Netzur {imbsCustLoaded ? `(${imbsCusts.length})` : ''}
              </button>
            </div>
          </div>
          {custTab === 'local' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={loadCustomers} disabled={custLoading}>
                <Users size={13} /> {custLoading ? 'Loading…' : custLoaded ? 'Reload' : 'Load Local Customers'}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSyncStatusBulk}
                disabled={syncingStatus}
                title="Pull expiry date, package, and active status from Netzur for every customer linked to Netzur — fixes stale broadband stats"
              >
                <RefreshCw size={13} /> {syncingStatus ? 'Syncing…' : 'Sync All Status & Expiry'}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handlePullAddressBulk}
                disabled={pullingAddress}
                title="Replace Billing Address with whatever Netzur has on file, for every customer linked to Netzur"
              >
                <Download size={13} /> {pullingAddress ? 'Replacing…' : 'Auto-Pull & Replace Billing Address'}
              </button>
            </div>
          )}
          {custTab === 'imbs' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={loadImbsCustomers} disabled={imbsCustLoading || imbsImporting}>
                <Wifi size={13} /> {imbsCustLoading ? 'Loading…' : imbsCustLoaded ? 'Reload Netzur' : 'Load Netzur Customers'}
              </button>
              {imbsCustLoaded && imbsCusts.length > 0 && (
                <>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowFieldPicker(v => !v)}
                    title="Choose which fields to import"
                  >
                    <Settings size={13} /> Fields ({importFields.size + 1}/{IMPORT_FIELDS.length + 1})
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ background: 'var(--cb-16a34a)', borderColor: 'var(--cb-16a34a)' }}
                    onClick={handleImportSubscribers}
                    disabled={imbsImporting}
                  >
                    <Download size={13} /> {imbsImporting ? 'Importing…' : `Import (${imbsCusts.length})`}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {syncStatusResult && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6,
            padding: '10px 14px', marginBottom: 16, fontSize: '.82rem',
          }}>
            <span style={{ fontWeight: 700, color: 'var(--c-16a34a)' }}>{syncStatusResult.synced} synced</span>
            {syncStatusResult.skipped > 0 && <span style={{ color: 'var(--c-d97706)' }}>{syncStatusResult.skipped} skipped</span>}
            <span style={{ color: 'var(--c-6b7280)' }}>of {syncStatusResult.total} Netzur-linked customers</span>
            {syncStatusResult.errors?.length > 0 && (
              <details style={{ marginLeft: 'auto' }}>
                <summary style={{ cursor: 'pointer', color: 'var(--c-6b7280)' }}>Show skipped details</summary>
                <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 6 }}>
                  {syncStatusResult.errors.map((e, i) => (
                    <div key={i} style={{ padding: '3px 0', borderTop: i > 0 ? '1px solid var(--cb-e5e7eb)' : 'none' }}>
                      <strong>{e.customer}</strong> — {e.error}
                    </div>
                  ))}
                </div>
              </details>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => setSyncStatusResult(null)}><X size={12} /></button>
          </div>
        )}

        {pullAddressResult && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            background: 'var(--cb-f9fafb)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6,
            padding: '10px 14px', marginBottom: 16, fontSize: '.82rem',
          }}>
            <span style={{ fontWeight: 700, color: 'var(--c-16a34a)' }}>{pullAddressResult.pulled} replaced</span>
            {pullAddressResult.skipped > 0 && <span style={{ color: 'var(--c-d97706)' }}>{pullAddressResult.skipped} skipped</span>}
            <span style={{ color: 'var(--c-6b7280)' }}>of {pullAddressResult.total} Netzur-linked customers</span>
            {pullAddressResult.errors?.length > 0 && (
              <details style={{ marginLeft: 'auto' }}>
                <summary style={{ cursor: 'pointer', color: 'var(--c-6b7280)' }}>Show skipped details</summary>
                <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 6 }}>
                  {pullAddressResult.errors.map((e, i) => (
                    <div key={i} style={{ padding: '3px 0', borderTop: i > 0 ? '1px solid var(--cb-e5e7eb)' : 'none' }}>
                      <strong>{e.customer}</strong> — {e.error}
                    </div>
                  ))}
                </div>
              </details>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => setPullAddressResult(null)}><X size={12} /></button>
          </div>
        )}

        {/* ── Local Customers Tab ── */}
        {custTab === 'local' && (
          <>
            {!custLoaded && !custLoading && (
              <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--c-9ca3af)' }}>
                <Users size={38} style={{ marginBottom: 10, opacity: .35 }} />
                <div style={{ fontSize: '.88rem' }}>Click "Load Local Customers" to show broadband customers and their Netzur link status.</div>
              </div>
            )}
            {custLoading && (
              <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--c-9ca3af)', fontSize: '.85rem' }}>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} />Loading customers…
              </div>
            )}
            {custLoaded && !custLoading && (
              <>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                  {[
                    { key: 'all',      label: `All (${customers.length})` },
                    { key: 'linked',   label: `Linked (${linkedCount})` },
                    { key: 'unlinked', label: `Unlinked (${unlinkedCount})` },
                  ].map(f => (
                    <button key={f.key} onClick={() => setCustFilter(f.key)} style={{
                      padding: '3px 12px', borderRadius: 14, border: '1px solid',
                      cursor: 'pointer', fontSize: '.78rem', fontWeight: custFilter === f.key ? 700 : 400,
                      background: custFilter === f.key ? 'var(--primary)' : 'transparent',
                      color: custFilter === f.key ? 'var(--c-ffffff)' : 'var(--c-6b7280)',
                      borderColor: custFilter === f.key ? 'var(--primary)' : 'var(--cb-d1d5db)',
                    }}>
                      {f.label}
                    </button>
                  ))}
                  <div style={{ position: 'relative', flex: 1, maxWidth: 300, marginLeft: 'auto' }}>
                    <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-9ca3af)', pointerEvents: 'none' }} />
                    <input
                      className="form-control"
                      style={{ paddingLeft: 28, height: 32, fontSize: '.83rem' }}
                      placeholder="Name, mobile, account, PPPoE…"
                      value={custSearch}
                      onChange={e => setCustSearch(e.target.value)}
                    />
                  </div>
                </div>
                {filteredCustomers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--c-9ca3af)', fontSize: '.85rem' }}>No customers match the current filter.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--cb-e5e7eb)' }}>
                          {['Username', 'Name', 'Mobile', 'Plan', 'Netzur', 'Synced', 'Actions'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--c-374151)', fontSize: '.78rem', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCustomers.map(c => {
                          const linked = !!c.imbs?.subscriberId;
                          const username = c.broadband?.pppoeUsername || c.broadband?.username || '—';
                          return (
                            <tr key={c._id} style={{ borderBottom: '1px solid var(--cb-f3f4f6)' }}>
                              <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '.85rem', whiteSpace: 'nowrap' }}>
                                <button
                                  onClick={() => navigate(`/customers/${c._id}`)}
                                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--primary)', fontFamily: 'monospace', fontSize: '.85rem', fontWeight: 700 }}
                                >
                                  {username}
                                </button>
                                <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)', fontWeight: 400, fontFamily: 'sans-serif' }}>{c.customerId}</div>
                              </td>
                              <td style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{c.name}</td>
                              <td style={{ padding: '10px 12px', color: 'var(--c-6b7280)' }}>{c.mobile || '—'}</td>
                              <td style={{ padding: '10px 12px', color: 'var(--c-6b7280)', fontSize: '.8rem', whiteSpace: 'nowrap' }}>
                                {c.broadband?.plan?.displayName || c.broadband?.plan?.name || '—'}
                              </td>
                              <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                                {linked ? (
                                  <span style={{ ...badge(true), padding: '2px 8px', fontSize: '.73rem', gap: 4 }}>
                                    <Link2 size={10} /> #{c.imbs.subscriberId}
                                  </span>
                                ) : (
                                  <span style={{ ...badge(false), padding: '2px 8px', fontSize: '.73rem' }}>Unlinked</span>
                                )}
                              </td>
                              <td style={{ padding: '10px 12px', color: 'var(--c-9ca3af)', fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                                {c.imbs?.lastSynced ? new Date(c.imbs.lastSynced).toLocaleDateString('en-IN') : '—'}
                              </td>
                              <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                                {linked ? (
                                  <button
                                    className="btn btn-danger btn-sm"
                                    style={{ padding: '3px 10px', fontSize: '.78rem' }}
                                    disabled={unlinking === c._id}
                                    onClick={() => handleUnlink(c)}
                                  >
                                    <Unlink size={11} style={{ marginRight: 4 }} />
                                    {unlinking === c._id ? '…' : 'Unlink'}
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-primary btn-sm"
                                    style={{ padding: '3px 10px', fontSize: '.78rem' }}
                                    onClick={() => setLinkTarget(c)}
                                  >
                                    <Link2 size={11} style={{ marginRight: 4 }} />Link
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ padding: '8px 12px', fontSize: '.75rem', color: 'var(--c-9ca3af)' }}>
                      Showing {filteredCustomers.length} of {customers.length} broadband customers
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── Netzur Customers Tab ── */}
        {custTab === 'imbs' && (
          <>
            {/* Field picker panel */}
            {showFieldPicker && (
              <div style={{
                background: 'var(--cb-f8fafc)', border: '1px solid var(--cb-e2e8f0)', borderRadius: 8,
                padding: '16px 20px', marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--c-374151)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    Fields to Import
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ padding: '2px 10px', fontSize: '.75rem' }}
                      onClick={() => setImportFields(new Set(IMPORT_FIELDS.map(f => f.key)))}
                    >
                      Select All
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ padding: '2px 10px', fontSize: '.75rem' }}
                      onClick={() => setImportFields(new Set())}
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                {/* Name — always on */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px 20px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'default', opacity: .6 }}>
                    <input type="checkbox" checked readOnly style={{ marginTop: 3, accentColor: 'var(--primary)' }} />
                    <div>
                      <div style={{ fontSize: '.85rem', fontWeight: 600 }}>Full Name <span style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)', fontWeight: 400 }}>(required)</span></div>
                      <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)' }}>fullname → Customer.name</div>
                    </div>
                  </label>

                  {IMPORT_FIELDS.map(field => (
                    <label key={field.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={importFields.has(field.key)}
                        onChange={() => toggleField(field.key)}
                        style={{ marginTop: 3, accentColor: 'var(--primary)', cursor: 'pointer' }}
                      />
                      <div>
                        <div style={{ fontSize: '.85rem', fontWeight: 600 }}>{field.label}</div>
                        <div style={{ fontSize: '.72rem', color: 'var(--c-9ca3af)' }}>{field.note}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {!imbsCustLoaded && !imbsCustLoading && (
              <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--c-9ca3af)' }}>
                <Wifi size={38} style={{ marginBottom: 10, opacity: .35 }} />
                <div style={{ fontSize: '.88rem' }}>
                  Click "Load Netzur Customers" to fetch all subscribers from Netzur.
                  <br />
                  <span style={{ fontSize: '.8rem', color: 'var(--c-cbd5e1)' }}>
                    Also load Local tab first to see which ones are already linked.
                  </span>
                </div>
              </div>
            )}
            {imbsCustLoading && (
              <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--c-9ca3af)', fontSize: '.85rem' }}>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} />Loading Netzur subscribers…
              </div>
            )}
            {imbsCustLoaded && !imbsCustLoading && (
              <>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                  <span style={{ ...badge(true), padding: '2px 10px', fontSize: '.73rem' }}>{imbsLinkedCount} Linked</span>
                  <span style={{ ...badge(false), padding: '2px 10px', fontSize: '.73rem' }}>{imbsUnlinkedCount} Unlinked</span>
                  {!custLoaded && (
                    <button className="btn btn-secondary btn-sm" onClick={loadCustomers} disabled={custLoading}>
                      <Users size={12} /> Load Local Customers to see links
                    </button>
                  )}
                  <div style={{ position: 'relative', flex: 1, maxWidth: 320, marginLeft: 'auto' }}>
                    <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-9ca3af)', pointerEvents: 'none' }} />
                    <input
                      className="form-control"
                      style={{ paddingLeft: 28, height: 32, fontSize: '.83rem' }}
                      placeholder="Name, username, mobile, ID…"
                      value={imbsCustSearch}
                      onChange={e => setImbsCustSearch(e.target.value)}
                    />
                  </div>
                </div>
                {filteredImbsCusts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--c-9ca3af)', fontSize: '.85rem' }}>No subscribers found.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--cb-e5e7eb)' }}>
                          {['#', 'Name', 'Username', 'Mobile', 'Package', 'Status', 'Expiry', 'Linked To'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--c-374151)', fontSize: '.78rem', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredImbsCusts.map((sub, i) => {
                          const subId = String(sub.id || sub.subscriber_id || '');
                          const linkedCustomer = imbsLinkMap[subId];
                          return (
                            <tr key={subId || i} style={{ borderBottom: '1px solid var(--cb-f3f4f6)' }}>
                              <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '.78rem', color: 'var(--c-9ca3af)' }}>{subId}</td>
                              <td style={{ padding: '10px 12px', fontWeight: 600 }}>{sub.name || sub.fullname || sub.full_name || '—'}</td>
                              <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '.82rem', color: 'var(--c-6b7280)' }}>{sub.username || '—'}</td>
                              <td style={{ padding: '10px 12px', color: 'var(--c-6b7280)' }}>{sub.mobile_no || sub.mobile || '—'}</td>
                              <td style={{ padding: '10px 12px', color: 'var(--c-6b7280)', fontSize: '.82rem' }}>{sub.package?.name || sub.package_name || '—'}</td>
                              <td style={{ padding: '10px 12px' }}>
                                <span style={{ ...badge(sub.status === 'active'), padding: '2px 8px', fontSize: '.73rem' }}>{sub.status || '—'}</span>
                              </td>
                              <td style={{ padding: '10px 12px', color: 'var(--c-6b7280)', fontSize: '.82rem', whiteSpace: 'nowrap' }}>{sub.expiry_date || '—'}</td>
                              <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                                {linkedCustomer ? (
                                  <span style={{ ...badge(true), padding: '2px 8px', fontSize: '.73rem', gap: 4 }}>
                                    <Link2 size={10} /> {linkedCustomer.name}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--c-d1d5db)', fontSize: '.78rem' }}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div style={{ padding: '8px 12px', fontSize: '.75rem', color: 'var(--c-9ca3af)' }}>
                      Showing {filteredImbsCusts.length} of {imbsCusts.length} Netzur subscribers
                      {!custLoaded && ' · Load Local tab to see link status'}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ── Package Sync ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <span style={sectionTitle}>Packages / Plans</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={loadPackages} disabled={pkgLoading}>
              <Package size={13} /> {pkgLoading ? 'Loading…' : 'Load Netzur Packages'}
            </button>
            {packages.length > 0 && (
              <button className="btn btn-primary btn-sm" onClick={handleImport} disabled={importing}
                style={{ background: 'var(--cb-16a34a)', borderColor: 'var(--cb-16a34a)' }}>
                <Download size={13} /> {importing ? 'Importing…' : `Import to Plans (${packages.length})`}
              </button>
            )}
          </div>
        </div>
        {packages.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--cb-e5e7eb)' }}>
                  {['Name', 'Speed', 'Price', 'Validity', 'Status'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--c-374151)', fontSize: '.78rem', letterSpacing: '.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg, i) => (
                  <tr key={pkg.id || pkg.package_id || i} style={{ borderBottom: '1px solid var(--cb-f3f4f6)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{pkg.name || pkg.package_name || '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--c-6b7280)' }}>{pkg.download_speed || pkg.speed ? `${pkg.download_speed || pkg.speed} Mbps` : '—'}</td>
                    <td style={{ padding: '10px 12px' }}>₹{pkg.price || pkg.monthly_price || 0}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--c-6b7280)' }}>{pkg.validity_days || pkg.validity || 30} days</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ ...badge(pkg.status !== 'inactive'), padding: '2px 8px', fontSize: '.73rem' }}>{pkg.status || 'active'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: 'var(--c-9ca3af)', fontSize: '.85rem', padding: '16px 0' }}>
            Click "Load Netzur Packages" to fetch the package list from Netzur.
          </div>
        )}
      </div>

      {/* ── Subscriber Lookup ── */}
      <div style={card}>
        <div style={{ ...sectionTitle, display: 'block', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid var(--cb-e5e7eb)' }}>Subscriber Lookup</div>
        <p style={{ fontSize: '.82rem', color: 'var(--c-6b7280)', marginTop: 0 }}>
          Search Netzur subscribers by username or mobile to look up their status directly.
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--cb-d1d5db)', borderRadius: 4, fontSize: '.88rem' }}
            placeholder="Search by username or mobile number…"
            value={subSearch}
            onChange={e => setSubSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchSubscribers()}
          />
          <button className="btn btn-primary btn-sm" onClick={searchSubscribers} disabled={subLoading}>
            <Search size={13} /> {subLoading ? 'Searching…' : 'Search'}
          </button>
        </div>
        {subscribers.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--cb-e5e7eb)' }}>
                  {['ID', 'Name', 'Username', 'Mobile', 'Package', 'Status', 'Expiry'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--c-374151)', fontSize: '.78rem', letterSpacing: '.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subscribers.map((sub, i) => (
                  <tr key={sub.id || sub.subscriber_id || i} style={{ borderBottom: '1px solid var(--cb-f3f4f6)' }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: 'var(--c-6b7280)', fontSize: '.78rem' }}>{sub.id || sub.subscriber_id}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{sub.name || sub.fullname || sub.full_name || '—'}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace' }}>{sub.username || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{sub.mobile_no || sub.mobile || '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--c-6b7280)' }}>{sub.package?.name || sub.package_name || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ ...badge(sub.status === 'active'), padding: '2px 8px', fontSize: '.73rem' }}>{sub.status || '—'}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--c-6b7280)', fontSize: '.82rem' }}>{sub.expiry_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Link Modal ── */}
      {linkTarget && (
        <LinkModal
          customer={linkTarget}
          onClose={() => setLinkTarget(null)}
          onLinked={handleLinked}
        />
      )}
    </div>
  );
}
